import React, { useEffect, useState } from "react";
import styles from "./ManagePaymentCards.module.scss";
import { useLazyGetCustomerSavedCardsQuery, useAddNewCardMutation, useRemoveCardMutation } from "../../../../service/charge-service";
import { AddCreditCardIcon, VisaLogo, MastercardLogo, VisaColorLogo, DeleteIcon } from "../../../../assets/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const getCardIcon = (cardType: string) => {
	const lower = (cardType ?? "").toLowerCase();
	if (lower.includes("visa")) return <VisaColorLogo />;
	if (lower.includes("master")) return <MastercardLogo />;
	return <span className={styles.genericCardBadge}>{(cardType ?? "C").charAt(0).toUpperCase()}</span>;
};

export const ManagePaymentCards: React.FC = () => {
	const { t } = useTranslation();
	const [getCards, { data, isFetching }] = useLazyGetCustomerSavedCardsQuery();
	const [addNewCard, { isLoading: isAddingCard }] = useAddNewCardMutation();
	const [removeCard] = useRemoveCardMutation();
	const [deletingCardId, setDeletingCardId] = useState<string | null>(null);

	useEffect(() => {
		getCards();
	}, [getCards]);

	const handleAddCard = async () => {
		try {
			const returnUrl = `${window.location.origin}/wallet`;
			const result = await addNewCard({ returnUrl }).unwrap();
			window.location.href = result.url;
		} catch {
			toast.error(t("wallet.web.failedToInitiateCardSetup"));
		}
	};

	const handleDeleteCard = async (cardId: string) => {
		setDeletingCardId(cardId);
		try {
			await removeCard({ savedCardId: cardId }).unwrap();
			await getCards();
		} catch {
			toast.error(t("wallet.web.failedToRemoveCard"));
		} finally {
			setDeletingCardId(null);
		}
	};

	const cards = data?.savedCards ?? [];

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<span className={styles.title}>{t("payment.cards.manageCards")}</span>
			</div>
			<div className={styles.body}>
				{isFetching && (
					<>
						{[1, 2].map(i => (
							<div key={i} className={styles.skeletonRow} />
						))}
					</>
				)}

				{!isFetching && cards.map(card => (
					<div key={card.id} className={styles.cardRow}>
						<div className={styles.cardInfo}>
							<div className={styles.cardIcon}>{getCardIcon(card.cardType)}</div>
							<span className={styles.cardNumber}>{card.panNumber}</span>
						</div>
						<button
							className={styles.deleteBtn}
							onClick={() => handleDeleteCard(card.id)}
							disabled={deletingCardId === card.id}
							aria-label={t("wallet.web.removeCard")}
						>
							{deletingCardId === card.id
								? <span className={styles.deleteSpinner} />
								: <DeleteIcon />
							}
						</button>
					</div>
				))}

				<button
					className={styles.addCardRow}
					onClick={handleAddCard}
					disabled={isAddingCard}
				>
					<div className={styles.addCardIcon}>
						<AddCreditCardIcon />
					</div>
					<span className={styles.addCardLabel}>
						{isAddingCard ? t("wallet.web.settingUp") : t("payment.cards.addCard")}
					</span>
				</button>
			</div>
		</div>
	);
};
